import { motion } from 'framer-motion'
import { useInView } from './useInView'
import { Calendar, Cpu, Layers, Building2, Wrench, Globe } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useT } from '../i18n/LanguageContext'
import type { Dictionary } from '../i18n/types'

type FactKey = keyof Dictionary['about']['facts']

const factList: Array<{ icon: LucideIcon; key: FactKey }> = [
  { icon: Calendar, key: 'operatingSince' },
  { icon: Cpu, key: 'digitizationAi' },
  { icon: Layers, key: 'implementations' },
  { icon: Building2, key: 'crossSector' },
  { icon: Wrench, key: 'solutions' },
  { icon: Globe, key: 'international' },
]

export default function About() {
  const t = useT()
  const { ref, isInView } = useInView()

  return (
    <section id="about" className="relative pt-8 pb-24 min-h-[88vh] flex flex-col justify-center overflow-hidden">
      <div className="absolute inset-0 bg-radial-center" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mb-6"
        >
          <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-4">
            {t.about.eyebrow}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
            {t.about.titlePart1}
            <span className="text-gradient"> {t.about.titlePart2}</span>
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed">
            {t.about.intro}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {factList.map((item, i) => {
            const fact = t.about.facts[item.key]
            const Icon = item.icon
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                className="group relative p-5 rounded-xl bg-white/[0.05] border border-white/[0.08] hover:border-white/[0.14] transition-all hover:bg-white/[0.08]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-brand-600/10 flex items-center justify-center shrink-0 group-hover:bg-brand-600/20 transition-colors">
                    <Icon size={18} className="text-brand-400" />
                  </div>
                  <h3 className="text-base font-bold text-white">{fact.title}</h3>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{fact.description}</p>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
